import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Typography,
  Space,
  Spin,
  Divider,
  message,
  Statistic,
  Row,
  Col,
  Checkbox,
  Badge,
  Radio,
  AutoComplete,
} from 'antd';
import {
  ArrowLeftOutlined,
  EyeOutlined,
  RightOutlined,
  DownOutlined,
  SearchOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

interface Dataset {
  id: number;
  name: string;
  buckets: string[];
  type: string;
}

type SourceType = 'dataset' | 'session';

interface ModerationSessionOption {
  id: number;
  name: string;
  dataset_id: number;
  dataset_name: string;
  approved_count: number;
}

interface SizeCategory {
  value: string;
  label: string;
}

interface AspectRatioStat {
  aspect_ratio: string;
  count: number;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  label: string;
  values: FilterOption[];
  type: string;
  counts: { [key: string]: number };
}

interface CaptionFilters {
  datasetType: string;
  filters: { [key: string]: FilterConfig };
}

type FiltersStatus = 'idle' | 'loading' | 'computing' | 'ready' | 'error';

interface NumberFilter {
  min: number | null;
  max: number | null;
}

type FilterMode = 'include' | 'exclude';

interface CaptionFilterState {
  dropdowns: { [key: string]: string[] };
  booleans: { [key: string]: boolean | null };
  numbers: { [key: string]: NumberFilter };
  text: { [key: string]: string };
  modes: { [key: string]: FilterMode };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Source-site provenance fields imported from pornpics_galleries.db. Rendered
// as their own group below the main caption filters so the text inputs aren't
// interleaved with dropdowns.
const SOURCE_PROVENANCE_KEYS = ['gallery_source_id', 'source_tags'];

const formatCount = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toString();

// Hoisted out of the parent component so its identity is stable across renders.
// Otherwise every keystroke in a text input would unmount/remount the whole
// section tree, dropping focus and resetting the scroll position. State is
// shared via a Context so call sites don't need extra props.
interface FilterCtxValue {
  expanded: string[];
  modes: { [k: string]: FilterMode };
  onToggle: (sectionKey: string) => void;
  onModeChange: (filterKey: string, mode: FilterMode) => void;
}
const FilterCtx = React.createContext<FilterCtxValue>({
  expanded: [], modes: {},
  onToggle: () => undefined, onModeChange: () => undefined,
});

interface FilterSectionProps {
  title: string;
  sectionKey: string;
  children: React.ReactNode;
  selectedCount?: number;
  showModeToggle?: boolean;
  filterKey?: string;
}

const FilterSection: React.FC<FilterSectionProps> = React.memo(({
  title, sectionKey, children, selectedCount = 0, showModeToggle = false, filterKey,
}) => {
  const { expanded, modes, onToggle, onModeChange } = React.useContext(FilterCtx);
  const isExpanded = expanded.includes(sectionKey);
  const mode: FilterMode = (filterKey ? modes[filterKey] : undefined) || 'include';
  const isExcludeMode = mode === 'exclude';
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        onClick={() => onToggle(sectionKey)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 12px', cursor: 'pointer',
          backgroundColor: isExcludeMode && selectedCount > 0 ? '#fff1f0' : '#fafafa',
          borderRadius: 4, marginBottom: isExpanded ? 4 : 0,
          borderLeft: isExcludeMode && selectedCount > 0 ? '3px solid #ff4d4f' : 'none',
        }}
      >
        <Text strong style={{ fontSize: 12 }}>{title}</Text>
        <Space size={4}>
          {selectedCount > 0 && (
            <Badge count={isExcludeMode ? `-${selectedCount}` : selectedCount} size="small"
              style={{ backgroundColor: isExcludeMode ? '#ff4d4f' : '#6366f1' }} />
          )}
          {isExpanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
        </Space>
      </div>
      {isExpanded && (
        <div style={{ padding: '4px 0', maxHeight: 200, overflowY: 'auto' }}>
          {showModeToggle && filterKey && (
            <div style={{ padding: '4px 12px 8px', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
              <Space size={4}>
                <Button size="small" type={mode === 'include' ? 'primary' : 'default'} icon={<PlusCircleOutlined />}
                  onClick={(e) => { e.stopPropagation(); onModeChange(filterKey, 'include'); }}
                  style={{ fontSize: 11, padding: '0 8px' }}>Include</Button>
                <Button size="small" type={mode === 'exclude' ? 'primary' : 'default'} danger={mode === 'exclude'}
                  icon={<MinusCircleOutlined />}
                  onClick={(e) => { e.stopPropagation(); onModeChange(filterKey, 'exclude'); }}
                  style={{ fontSize: 11, padding: '0 8px' }}>Exclude</Button>
              </Space>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
});

interface FilterItemProps {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}

const FilterItem: React.FC<FilterItemProps> = React.memo(({ label, count, selected, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 12px', cursor: 'pointer',
      backgroundColor: selected ? '#f0f0ff' : 'transparent',
      borderRadius: 4,
    }}
  >
    <Space size={6}>
      <Checkbox checked={selected} />
      <Text style={{ fontSize: 12 }}>{label}</Text>
    </Space>
    <Text type="secondary" style={{ fontSize: 11 }}>{formatCount(count)}</Text>
  </div>
));

const SIZE_CATEGORIES: SizeCategory[] = [
  { value: 'extra_tiny', label: 'XTiny' },
  { value: 'tiny', label: 'Tiny' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra_large', label: 'XL' },
];

const emptyCaptionFilterState: CaptionFilterState = {
  dropdowns: {},
  booleans: {},
  numbers: {},
  text: {},
  modes: {},
};

const ModerationSessionCreate: React.FC = () => {
  const [form] = Form.useForm();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoStarting, setAutoStarting] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(null);
  const navigate = useNavigate();

  // Source: a regular dataset, or the approved images of another (parent) task
  const [sourceType, setSourceType] = useState<SourceType>('dataset');
  const [sessions, setSessions] = useState<ModerationSessionOption[]>([]);
  const [selectedSourceSessionId, setSelectedSourceSessionId] = useState<number | null>(null);

  // Filter state
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [sizeStats, setSizeStats] = useState<{ [key: string]: number }>({});
  const [selectedAspectRatios, setSelectedAspectRatios] = useState<string[]>([]);
  const [aspectRatioStats, setAspectRatioStats] = useState<AspectRatioStat[]>([]);
  const [captionFilters, setCaptionFilters] = useState<CaptionFilters | null>(null);
  const [selectedCaptionFilters, setSelectedCaptionFilters] = useState<CaptionFilterState>(emptyCaptionFilterState);
  const [expandedFilters, setExpandedFilters] = useState<string[]>(['size', 'ratio']);

  // Watermark limit
  const [maxPerWatermark, setMaxPerWatermark] = useState<number | null>(null);

  // Preview count
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);

  // Filter precompute status — when picking a large dataset for the first time
  // the backend kicks off FilterStatsWorker and we poll until counts are ready.
  const [filtersStatus, setFiltersStatus] = useState<FiltersStatus>('idle');
  const [filtersComputeStartedAt, setFiltersComputeStartedAt] = useState<number | null>(null);
  const [filtersElapsedSec, setFiltersElapsedSec] = useState(0);
  const selectedDatasetIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedDatasetIdRef.current = selectedDatasetId;
  }, [selectedDatasetId]);
  useEffect(() => {
    if (filtersStatus !== 'computing' || !filtersComputeStartedAt) {
      setFiltersElapsedSec(0);
      return;
    }
    const tick = () => setFiltersElapsedSec(Math.floor((Date.now() - filtersComputeStartedAt) / 1000));
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [filtersStatus, filtersComputeStartedAt]);

  // Previously-used auto-mod run labels, for the "choose existing or type new"
  // collection-prefix picker.
  const [prefixOptions, setPrefixOptions] = useState<string[]>([]);
  // Available classifier profiles for the auto-moderation picker.
  const [classifierOptions, setClassifierOptions] = useState<{ key: string; name: string }[]>([]);

  useEffect(() => {
    fetchDatasets();
    fetchSessions();
    axios.get(`${API_URL}/auto-moderation/prefixes`)
      .then(res => setPrefixOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* non-fatal: picker just has no suggestions */ });
    axios.get(`${API_URL}/auto-moderation/classifiers`)
      .then(res => setClassifierOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* non-fatal: falls back to server default */ });
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/moderation-sessions`);
      // Only tasks that have approved at least one image can feed a child funnel
      setSessions(response.data.filter((s: ModerationSessionOption) => s.approved_count > 0));
    } catch (error) {
      // non-fatal: the session-source option simply has no candidates
    }
  };

  const handleSourceTypeChange = (next: SourceType) => {
    setSourceType(next);
    setSelectedDatasetId(null);
    setSelectedSourceSessionId(null);
    setPreviewCount(null);
    form.setFieldsValue({ dataset_id: undefined, source_session_id: undefined });
  };

  const handleSourceSessionChange = async (sessionId: number) => {
    setSelectedSourceSessionId(sessionId);
    form.setFieldsValue({ source_session_id: sessionId });
    try {
      // Inherit the parent's dataset so the dataset-scoped filter controls load
      const response = await axios.get(`${API_URL}/moderation-sessions/${sessionId}`);
      setSelectedDatasetId(response.data.dataset_id);
    } catch (error) {
      message.error('Failed to load source task');
    }
  };

  useEffect(() => {
    if (selectedDatasetId) {
      fetchFilterData(selectedDatasetId);
      // Reset filters on dataset change
      setSelectedSizes([]);
      setSelectedAspectRatios([]);
      setSelectedCaptionFilters(emptyCaptionFilterState);
      setMaxPerWatermark(null);
      setPreviewCount(null);
    }
  }, [selectedDatasetId]);

  const fetchDatasets = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets`);
      setDatasets(response.data);
    } catch (error) {
      message.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterData = async (datasetId: number) => {
    setFiltersStatus('loading');
    try {
      const [sizeRes, arRes, cfRes] = await Promise.all([
        axios.get(`${API_URL}/datasets/${datasetId}/size-stats`),
        axios.get(`${API_URL}/datasets/${datasetId}/aspect-ratio-stats`),
        axios.get(`${API_URL}/datasets/${datasetId}/caption-filters`),
      ]);
      setSizeStats(sizeRes.data);
      const stats = Object.entries(arRes.data).map(([ratio, count]) => ({
        aspect_ratio: ratio,
        count: count as number,
      }));
      stats.sort((a, b) => b.count - a.count);
      setAspectRatioStats(stats);
      setCaptionFilters(cfRes.data);

      if (cfRes.data?.status === 'computing') {
        setFiltersStatus('computing');
        setFiltersComputeStartedAt(Date.now());
        pollFilterStatsReady(datasetId);
      } else {
        setFiltersStatus('ready');
        setFiltersComputeStartedAt(null);
      }
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
      setFiltersStatus('error');
    }
  };

  const pollFilterStatsReady = async (datasetId: number) => {
    // Poll the admin status endpoint until the FilterStatsWorker finishes,
    // then re-fetch caption-filters so the UI gets real counts.
    let attempts = 0;
    const maxAttempts = 180; // ~6 min at 2s intervals
    const interval = 2000;

    const initialStatus = await axios
      .get(`${API_URL}/admin/filter-stats/${datasetId}`)
      .then((r) => r.data)
      .catch(() => null);
    const initialComputedAt = initialStatus?.lastComputedAt ?? null;

    const tick = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API_URL}/admin/filter-stats/${datasetId}`);
        const done =
          !data.isRunning &&
          (data.statsCount > 0) &&
          data.lastComputedAt !== initialComputedAt;

        if (done) {
          const cfRes = await axios.get(`${API_URL}/datasets/${datasetId}/caption-filters`);
          // Make sure the user hasn't switched datasets while we waited.
          if (selectedDatasetIdRef.current === datasetId) {
            setCaptionFilters(cfRes.data);
            setFiltersStatus('ready');
            setFiltersComputeStartedAt(null);
          }
          return;
        }
      } catch (err) {
        // Non-fatal: keep retrying.
      }

      if (attempts >= maxAttempts) {
        setFiltersStatus('error');
        setFiltersComputeStartedAt(null);
        return;
      }
      setTimeout(tick, interval);
    };

    setTimeout(tick, interval);
  };

  const buildFilterParams = () => {
    const params: any = {};
    if (selectedSizes.length > 0) params.size = selectedSizes;
    if (selectedAspectRatios.length > 0) params.aspectRatio = selectedAspectRatios;

    const captionFilterObj: any = {};
    for (const [key, values] of Object.entries(selectedCaptionFilters.dropdowns)) {
      if (values.length > 0) {
        captionFilterObj[key] = {
          values,
          exclude: selectedCaptionFilters.modes[key] === 'exclude'
        };
      }
    }
    for (const [key, value] of Object.entries(selectedCaptionFilters.booleans)) {
      if (value !== null && value !== undefined) {
        // Check if this is a boolean flag under a parent group
        let exclude = false;
        if (captionFilters) {
          for (const [filterKey, config] of Object.entries(captionFilters.filters)) {
            if (config.type === 'boolean_flags' && config.values?.some(v => v.value === key)) {
              exclude = selectedCaptionFilters.modes[filterKey] === 'exclude';
              break;
            }
          }
        }
        captionFilterObj[key] = { value, exclude };
      }
    }
    for (const [key, range] of Object.entries(selectedCaptionFilters.numbers)) {
      if (range && (range.min !== null || range.max !== null)) {
        captionFilterObj[key] = range;
      }
    }
    for (const [key, value] of Object.entries(selectedCaptionFilters.text)) {
      if (value && value.length > 0) {
        captionFilterObj[key] = value;
      }
    }
    if (Object.keys(captionFilterObj).length > 0) {
      params.captionFilters = captionFilterObj;
    }

    if (maxPerWatermark) {
      params.maxPerWatermark = maxPerWatermark;
    }

    return params;
  };

  // Source identifiers for the create/preview request bodies
  const sourceBody = () =>
    sourceType === 'session'
      ? { source_type: 'session', source_session_id: selectedSourceSessionId }
      : { dataset_id: selectedDatasetId };

  const sourceReady = sourceType === 'session' ? !!selectedSourceSessionId : !!selectedDatasetId;

  const handlePreviewCount = async () => {
    if (!sourceReady) return;
    setCountLoading(true);
    try {
      const filterParams = buildFilterParams();
      const response = await axios.post(`${API_URL}/moderation-sessions/preview-count`, {
        ...sourceBody(),
        filter_params: Object.keys(filterParams).length > 0 ? filterParams : undefined
      });
      setPreviewCount(response.data.count || 0);
    } catch (error) {
      message.error('Failed to get image count');
    } finally {
      setCountLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!sourceReady) return;
    setSaving(true);
    try {
      const filterParams = buildFilterParams();
      const response = await axios.post(`${API_URL}/moderation-sessions`, {
        name: values.name,
        ...sourceBody(),
        filter_params: filterParams,
      });
      message.success(`Session created with ${response.data.total_images} images`);
      navigate(`/moderation/${response.data.id}`);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create session');
    } finally {
      setSaving(false);
    }
  };

  // Create an auto-moderation session from the same filter, then jump to its
  // progress screen (it does not auto-start — the user starts it there).
  const handleAutoModerate = async () => {
    if (!sourceReady) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return; // missing required fields (e.g. name) — antd shows the errors
    }
    setAutoStarting(true);
    try {
      const filterParams = buildFilterParams();
      const response = await axios.post(`${API_URL}/auto-moderation-sessions`, {
        name: values.name,
        ...sourceBody(),
        filter_params: filterParams,
        collection_prefix: values.collection_prefix || 'auto',
        classifier: values.classifier || 'default',
      });
      message.success(`Auto-moderation session created with ${response.data.total_images} images`);
      navigate(`/auto-moderation/${response.data.id}`);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create auto-moderation session');
    } finally {
      setAutoStarting(false);
    }
  };

  // Filter handlers
  const toggleFilterSection = useCallback((key: string) => {
    setExpandedFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  const handleSizeFilterChange = (size: string) => {
    setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
    setPreviewCount(null);
  };

  const handleAspectRatioFilterChange = (ratio: string) => {
    setSelectedAspectRatios(prev => prev.includes(ratio) ? prev.filter(r => r !== ratio) : [...prev, ratio]);
    setPreviewCount(null);
  };

  const handleCaptionFilterChange = (filterKey: string, value: string) => {
    setSelectedCaptionFilters(prev => {
      const current = prev.dropdowns[filterKey] || [];
      if (current.includes(value)) {
        return { ...prev, dropdowns: { ...prev.dropdowns, [filterKey]: current.filter(v => v !== value) } };
      } else {
        return { ...prev, dropdowns: { ...prev.dropdowns, [filterKey]: [...current, value] } };
      }
    });
    setPreviewCount(null);
  };

  const handleBooleanFilterChange = (filterKey: string, value: boolean | null) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      booleans: { ...prev.booleans, [filterKey]: prev.booleans[filterKey] === value ? null : value }
    }));
    setPreviewCount(null);
  };

  const handleNumberFilterChange = (filterKey: string, type: 'min' | 'max', value: number | null) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      numbers: {
        ...prev.numbers,
        [filterKey]: { ...(prev.numbers[filterKey] || { min: null, max: null }), [type]: value }
      }
    }));
    setPreviewCount(null);
  };

  const handleTextFilterChange = (filterKey: string, value: string) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      text: { ...prev.text, [filterKey]: value }
    }));
    setPreviewCount(null);
  };

  const handleFilterModeChange = useCallback((filterKey: string, mode: FilterMode) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      modes: { ...prev.modes, [filterKey]: mode }
    }));
    setPreviewCount(null);
  }, []);

  // Stable context value reused by the hoisted FilterSection so its identity
  // doesn't churn on every keystroke.
  const filterCtxValue = React.useMemo<FilterCtxValue>(() => ({
    expanded: expandedFilters,
    modes: selectedCaptionFilters.modes,
    onToggle: toggleFilterSection,
    onModeChange: handleFilterModeChange,
  }), [expandedFilters, selectedCaptionFilters.modes]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/moderation')} style={{ marginBottom: 16 }}>
            Back to Moderation Sessions
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EyeOutlined style={{ fontSize: 28, color: '#6366f1' }} />
            <Title level={2} style={{ margin: 0 }}>Create Moderation Session</Title>
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="name" label="Session Name" rules={[{ required: true, message: 'Please enter a session name' }]}>
                <Input placeholder="e.g., Review Dataset Quality" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Image Source">
                <Radio.Group
                  value={sourceType}
                  onChange={(e) => handleSourceTypeChange(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="dataset">Dataset</Radio.Button>
                  <Radio.Button value="session">Approved images from a task</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="classifier"
                label="Auto-moderation classifier"
                tooltip="What the auto-moderation reasoning chain classifies. 'General' = POV / skin texture / selfie / camera angle; 'Blowjob' = is_blowjob true/false. (Manual sessions ignore this.)"
                initialValue="default"
              >
                <Select
                  options={classifierOptions.map(c => ({ value: c.key, label: c.name }))}
                  placeholder="General"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="collection_prefix"
                label="Auto-moderation run label"
                tooltip="Auto-moderation routes classified images into <label>-<category> collections (e.g. <label>-blowjob). Use a unique label per run to keep results isolated; reuse a label to add to a previous run. (Manual sessions ignore this.)"
                initialValue="auto"
              >
                <AutoComplete
                  placeholder="auto"
                  allowClear
                  options={prefixOptions.map(p => ({ value: p }))}
                  filterOption={(input, option) =>
                    (option?.value as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          {sourceType === 'dataset' ? (
            <Form.Item name="dataset_id" label="Dataset" rules={[{ required: true, message: 'Please select a dataset' }]}>
              <Select placeholder="Select dataset" onChange={(v: number) => {
                setSelectedDatasetId(v);
                form.setFieldsValue({ dataset_id: v });
              }}>
                {datasets.map(d => (
                  <Option key={d.id} value={d.id}>{d.name} ({d.type})</Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item name="source_session_id" label="Source Task (uses its approved images)"
              rules={[{ required: true, message: 'Please select a source task' }]}>
              <Select placeholder="Select a task to funnel from" onChange={handleSourceSessionChange}
                notFoundContent="No tasks with approved images yet">
                {sessions.map(s => (
                  <Option key={s.id} value={s.id}>{s.name} — {s.approved_count} approved ({s.dataset_name})</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedDatasetId && (
            <>
              <Divider>Filters (Optional)</Divider>
              {sourceType === 'session' && (
                <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 12 }}>
                  Filter counts below reflect the whole dataset. The actual queue is the source task's
                  approved images narrowed by these filters — use Preview Count for the accurate number.
                </Text>
              )}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 12, marginRight: 8 }}>Max images per watermark / gallery:</Text>
                <InputNumber
                  size="small"
                  min={1}
                  placeholder="No limit"
                  value={maxPerWatermark}
                  onChange={(v) => { setMaxPerWatermark(v); setPreviewCount(null); }}
                  style={{ width: 120 }}
                />
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  Limits per watermark (OF) or per gallery (Pornpics) and shuffles the result. Leave empty to round-robin every image.
                </Text>
              </div>
              {filtersStatus === 'computing' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', marginBottom: 12,
                  background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, fontSize: 12,
                }}>
                  <Spin size="small" />
                  <Text>
                    Computing filter counts for this dataset… ({filtersElapsedSec}s elapsed). You can pick filters now; counts will appear once ready.
                  </Text>
                </div>
              )}
              {filtersStatus === 'error' && (
                <div style={{
                  padding: '8px 12px', marginBottom: 12,
                  background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4, fontSize: 12,
                }}>
                  <Text type="danger">Failed to compute filter counts. You can still pick filters and use Preview Count.</Text>
                </div>
              )}
              <FilterCtx.Provider value={filterCtxValue}>
              <div style={{ maxHeight: 500, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, marginBottom: 16 }}>
                {/* Size Filter */}
                <FilterSection title="Size" sectionKey="size" selectedCount={selectedSizes.length}>
                  {SIZE_CATEGORIES.map(cat => {
                    const count = sizeStats[cat.value] || 0;
                    if (count === 0) return null;
                    return (
                      <FilterItem key={cat.value} label={cat.label} count={count}
                        selected={selectedSizes.includes(cat.value)}
                        onClick={() => handleSizeFilterChange(cat.value)} />
                    );
                  })}
                </FilterSection>

                {/* Aspect Ratio Filter */}
                <FilterSection title="Aspect Ratio" sectionKey="ratio" selectedCount={selectedAspectRatios.length}>
                  {aspectRatioStats.map(stat => (
                    <FilterItem key={stat.aspect_ratio} label={stat.aspect_ratio} count={stat.count}
                      selected={selectedAspectRatios.includes(stat.aspect_ratio)}
                      onClick={() => handleAspectRatioFilterChange(stat.aspect_ratio)} />
                  ))}
                </FilterSection>

                {/* Caption Filters */}
                {captionFilters && Object.entries(captionFilters.filters).filter(([k]) => !SOURCE_PROVENANCE_KEYS.includes(k)).map(([filterKey, filterConfig]) => {
                  const filterType = filterConfig.type;
                  // While the precompute worker is running every count is 0 — keep
                  // filters visible so the user can pre-select options.
                  const computing = filtersStatus === 'computing';

                  if (filterType === 'boolean') {
                    const counts = filterConfig.counts || {};
                    const trueCount = counts['true'] || 0;
                    const falseCount = counts['false'] || 0;
                    if (trueCount === 0 && falseCount === 0 && !computing) return null;
                    const currentValue = selectedCaptionFilters.booleans[filterKey];
                    const selectedCount = currentValue !== null && currentValue !== undefined ? 1 : 0;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                        {(trueCount > 0 || computing) && <FilterItem label="Yes" count={trueCount} selected={currentValue === true}
                          onClick={() => handleBooleanFilterChange(filterKey, true)} />}
                        {(falseCount > 0 || computing) && <FilterItem label="No" count={falseCount} selected={currentValue === false}
                          onClick={() => handleBooleanFilterChange(filterKey, false)} />}
                      </FilterSection>
                    );
                  }

                  if (filterType === 'boolean_flags') {
                    const counts = filterConfig.counts || {};
                    const anyNonZero = Object.values(counts).some(c => (c as number) > 0);
                    if (!anyNonZero && !computing) return null;
                    const selectedFlags = Object.entries(selectedCaptionFilters.booleans)
                      .filter(([k, v]) => filterConfig.values?.some(opt => opt.value === k) && v === true).length;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey}
                        selectedCount={selectedFlags} showModeToggle filterKey={filterKey}>
                        {filterConfig.values?.map(option => {
                          const count = counts[option.value] || 0;
                          if (count === 0 && !computing) return null;
                          return (
                            <FilterItem key={option.value} label={option.label} count={count as number}
                              selected={selectedCaptionFilters.booleans[option.value] === true}
                              onClick={() => handleBooleanFilterChange(option.value, true)} />
                          );
                        })}
                      </FilterSection>
                    );
                  }

                  if (filterType === 'min_max') {
                    const config = filterConfig as any;
                    if (config.total === 0 && !computing) return null;
                    const currentRange = selectedCaptionFilters.numbers[filterKey];
                    const selectedCount = currentRange && (currentRange.min !== null || currentRange.max !== null) ? 1 : 0;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                        <div style={{ padding: '4px 12px' }}>
                          <Space size={8}>
                            <InputNumber size="small" placeholder="Min" min={config.min || 0} max={config.max || 100}
                              value={currentRange?.min}
                              onChange={(v) => handleNumberFilterChange(filterKey, 'min', v)}
                              style={{ width: 70 }} />
                            <span>-</span>
                            <InputNumber size="small" placeholder="Max" min={config.min || 0} max={config.max || 100}
                              value={currentRange?.max}
                              onChange={(v) => handleNumberFilterChange(filterKey, 'max', v)}
                              style={{ width: 70 }} />
                          </Space>
                        </div>
                      </FilterSection>
                    );
                  }

                  if (filterType === 'text_search') {
                    const currentText = selectedCaptionFilters.text[filterKey] || '';
                    const selectedCount = currentText.length > 0 ? 1 : 0;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                        <div style={{ padding: '4px 12px' }}>
                          <Input.Search size="small" placeholder={`Search ${filterConfig.label.toLowerCase()}...`}
                            value={currentText}
                            onChange={(e) => handleTextFilterChange(filterKey, e.target.value)}
                            onSearch={(v) => handleTextFilterChange(filterKey, v)}
                            allowClear style={{ width: '100%' }} />
                        </div>
                      </FilterSection>
                    );
                  }

                  if (filterType === 'array_dropdown') {
                    // Render same as dropdown but `values` may be derived
                    // dynamically from observed counts (no static enum).
                    const counts = filterConfig.counts || {};
                    const dynamicValues = filterConfig.values && filterConfig.values.length > 0
                      ? filterConfig.values
                      : Object.keys(counts).map(v => ({ value: v, label: v }));
                    const sortedValues = [...dynamicValues].sort((a, b) => (counts[b.value] || 0) - (counts[a.value] || 0));
                    const anyVisible = sortedValues.some(v => (counts[v.value] || 0) > 0);
                    if (!anyVisible && !computing) return null;
                    const selectedCount = (selectedCaptionFilters.dropdowns[filterKey] || []).length;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey}
                        selectedCount={selectedCount} showModeToggle filterKey={filterKey}>
                        {sortedValues.map(option => {
                          const count = counts[option.value] || 0;
                          if (count === 0 && !computing) return null;
                          return (
                            <FilterItem key={option.value} label={option.label} count={count as number}
                              selected={(selectedCaptionFilters.dropdowns[filterKey] || []).includes(option.value)}
                              onClick={() => handleCaptionFilterChange(filterKey, option.value)} />
                          );
                        })}
                      </FilterSection>
                    );
                  }

                  // Dropdown filter (default)
                  const hasValues = filterConfig.values && Object.values(filterConfig.counts || {}).some(c => (c as number) > 0);
                  if (!hasValues && !computing) return null;
                  const selectedCount = (selectedCaptionFilters.dropdowns[filterKey] || []).length;
                  return (
                    <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey}
                      selectedCount={selectedCount} showModeToggle filterKey={filterKey}>
                      {filterConfig.values?.map(option => {
                        const count = filterConfig.counts?.[option.value] || 0;
                        if (count === 0 && !computing) return null;
                        return (
                          <FilterItem key={option.value} label={option.label} count={count as number}
                            selected={(selectedCaptionFilters.dropdowns[filterKey] || []).includes(option.value)}
                            onClick={() => handleCaptionFilterChange(filterKey, option.value)} />
                        );
                      })}
                    </FilterSection>
                  );
                })}
              </div>

              {/* Source Provenance (Pornpics) — pulled out of the main caption-filter
                  loop so the text inputs sit in their own block. All three are
                  text_search filters; they share the same Input.Search UX. */}
              {captionFilters && SOURCE_PROVENANCE_KEYS.some(k => k in captionFilters.filters) && (
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, marginBottom: 16 }}>
                  <Text strong style={{ display: 'block', fontSize: 12, padding: '4px 12px 8px', color: '#6366f1' }}>
                    Source Provenance (Pornpics)
                  </Text>
                  {SOURCE_PROVENANCE_KEYS.map(filterKey => {
                    const filterConfig = captionFilters.filters[filterKey];
                    if (!filterConfig) return null;
                    const currentText = selectedCaptionFilters.text[filterKey] || '';
                    const selectedCount = currentText.length > 0 ? 1 : 0;
                    return (
                      <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                        <div style={{ padding: '4px 12px' }}>
                          <Input
                            size="small"
                            placeholder={`Search ${filterConfig.label.toLowerCase()}...`}
                            value={currentText}
                            onChange={(e) => handleTextFilterChange(filterKey, e.target.value)}
                            allowClear
                            style={{ width: '100%' }}
                          />
                        </div>
                      </FilterSection>
                    );
                  })}
                </div>
              )}
              </FilterCtx.Provider>

              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col>
                  <Button icon={<SearchOutlined />} onClick={handlePreviewCount} loading={countLoading}>
                    Preview Count
                  </Button>
                </Col>
                {previewCount !== null && (
                  <Col>
                    <Statistic title="Matching Images" value={previewCount}
                      valueStyle={{ color: previewCount > 0 ? '#52c41a' : '#ff4d4f' }} />
                  </Col>
                )}
              </Row>
            </>
          )}

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving} size="large"
                disabled={!sourceReady}>
                Create Session
              </Button>
              <Button icon={<ThunderboltOutlined />} onClick={handleAutoModerate}
                loading={autoStarting} size="large" disabled={!sourceReady}
                title="Classify this selection with the Nebius vision funnel instead of reviewing by hand">
                Auto-moderate (Nebius)
              </Button>
              <Button onClick={() => navigate('/moderation')} size="large">Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ModerationSessionCreate;
